import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import '@/lib/chartjs-register';
import { convertChartOptions } from '@/lib/chartjs-register';
import { ChartWidget } from '@/components/ui/shared/ChartWidget';
import ColorJson from '../../src/whitelabel.json';

/**
 * MultiStackBarChart - Functional component for Multi-stack Bar/List toggle widget
 * 
 * Replaces the class-based MultiStackBar_List component with modern React patterns
 * Supports stacked bar charts with multiple datasets
 */
function MultiStackBarChart({ data, _Index }) {
    // State - only show loader if data is ready to fetch
    const [chartData, setChartData] = useState(null);
    const [listData, setListData] = useState([]);
    const [listHeaders, setListHeaders] = useState([]);
    const [legendItems, setLegendItems] = useState([]);
    const [sumOfLegendValue, setSumOfLegendValue] = useState('');
    const [isLoading, setIsLoading] = useState(!!(data?.RequestJson && data?.url));
    const [error, setError] = useState(null);
    const [noDataMessage, setNoDataMessage] = useState('');
    const [hasNegativeValues, setHasNegativeValues] = useState(false);

    // Refs
    const abortControllerRef = useRef(null);
    const prevRequestJsonRef = useRef(null);

    // Determine if RxHacker theme
    const isRxHacker = useMemo(() => {
        const host = window.location.host.split('.').slice(-2).join('.').toLowerCase();
        return host === "rxhacker.com" || 
               host === "localhost:5464" || 
               window.location.host.toLowerCase() === "tempfilter.23byt.es" || 
               window.location.host.toLowerCase() === "reporting.nulifespanrx.com";
    }, []);

    // Get theme colors
    const themeColors = useMemo(() => {
        if (isRxHacker) {
            return {
                primary: ColorJson.ApplicationColor.Rxhacker_primary_color,
                secondary: ColorJson.ApplicationColor.Rxhacker_secondary_color,
                extra: ColorJson.ApplicationColor.Rxhacker_extra_color,
                hover: ColorJson.ApplicationColor.Rxhacker_extra_color,
            };
        }
        return {
            primary: ColorJson.ApplicationColor.Glic_primary_color,
            secondary: ColorJson.ApplicationColor.Glic_secondary_color,
            extra: ColorJson.ApplicationColor.Glic_extra_color,
            hover: '#FF8B6C',
        };
    }, [isRxHacker]);

    // Handle API response
    const handleResponse = useCallback((response) => {
        return response.text().then(text => {
            try {
                return text && JSON.parse(text);
            } catch {
                return [];
            }
        });
    }, []);

    // Export widget data as CSV
    const handleExport = useCallback(() => {
        try {
            const widgetTitle = data.title || 'export';
            const tableData = [];
            
            if (listHeaders.length > 0) {
                tableData.push(listHeaders.join(','));
            }
            
            listData.forEach(row => {
                const rowData = row.map((cell, cellIndex) => {
                    let cellValue = String(cell || '').split(',').join('');
                    
                    // Check if this looks like a date in format XX/XX or XX/XX/XXXX
                    // Add a leading space to prevent Excel from auto-formatting dates
                    if (cellIndex === 0 && /^\d{1,2}\/\d{1,2}(\/\d{2,4})?$/.test(cellValue)) {
                        cellValue = ` ${cellValue}`;
                    }
                    
                    return cellValue;
                });
                tableData.push(rowData.join(','));
            });
            
            const csvContent = tableData.join('\n');
            const blob = new Blob([csvContent], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.download = `${widgetTitle}.csv`;
            link.href = url;
            document.body.appendChild(link);
            
            setTimeout(() => {
                link.click();
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 0);
        } catch (err) {
        }
    }, [data.title, listData, listHeaders]);

    // Build chart options
    const chartOptions = useMemo(() => ({
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                display: false,
                position: 'top',
                labels: {
                    usePointStyle: true,
                    padding: 15,
                    font: {
                        size: 11,
                    },
                    color: 'var(--foreground)',
                },
            },
            tooltip: {
                enabled: true,
                backgroundColor: 'rgba(255, 255, 255, 0.95)',
                titleColor: '#1d252d',
                bodyColor: '#1d252d',
                borderColor: '#e2e8f0',
                borderWidth: 1,
                padding: 12,
                cornerRadius: 8,
                titleFont: {
                    size: 12,
                    weight: 'normal',
                },
                bodyFont: {
                    size: 13,
                    weight: 'bold',
                },
                callbacks: {
                    label: (context) => {
                        const value = context.parsed.y;
                        const formatted = typeof value === 'number' 
                            ? value.toLocaleString() 
                            : value;
                        return `${context.dataset.label}: ${formatted}`;
                    },
                },
            },
        },
        scales: {
            x: {
                stacked: true,
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    display: false,
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                },
                title: {
                    display: true,
                    text: 'Date',
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                },
            },
            y: {
                stacked: true,
                beginAtZero: true,
                border: {
                    display: false,
                },
                grid: {
                    display: false,
                    drawBorder: false,
                },
                ticks: {
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                    callback: (value) => {
                        if (Math.abs(value) >= 1000000) {
                            return '$' + (value / 1000000).toFixed(1) + 'M';
                        } else if (Math.abs(value) >= 1000) {
                            return '$' + (value / 1000).toFixed(0) + 'K';
                        }
                        return '$' + value;
                    },
                },
                title: {
                    display: true,
                    text: 'Commission',
                    color: '#94a3b8',
                    font: {
                        size: 11,
                    },
                },
            },
        },
        interaction: {
            intersect: false,
            mode: 'index',
        },
    }), []);

    // Fetch data
    const fetchData = useCallback(async () => {
        if (abortControllerRef.current) {
            abortControllerRef.current.abort();
        }

        const controller = new AbortController();
        abortControllerRef.current = controller;

        setIsLoading(true);
        setError(null);
        setNoDataMessage('Loading. Please wait.');

        try {
            const response = await fetch(data.url, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'requestHeader': data.RequestJson1,
                    'X-Browser-Url': window.location.href,
                },
                body: data.RequestJson,
                signal: controller.signal,
            });

            const apidata = await handleResponse(response);

            if (controller.signal.aborted) return;

            if (!apidata || !apidata[0]) {
                setNoDataMessage('No data available');
                setIsLoading(false);
                return;
            }

            // Process legend data
            try {
                const legends = [];
                if (apidata[0].legend?.length > 0) {
                    apidata[0].legend.forEach((leg, idx) => {
                        // Hide 'PARENT-SUB-DISTRIBUTOR' legend
                        if (leg.legendText && leg.legendText.toUpperCase() === 'PARENT-SUB-DISTRIBUTOR') {
                            return; // Skip this legend item
                        }
                        
                        legends.push({
                            text: leg.legendText,
                            value: leg.legendValue,
                            color: idx === 0 ? themeColors.secondary : 
                                   idx === 1 ? themeColors.primary : 
                                   themeColors.extra,
                        });
                    });
                }
                setLegendItems(legends);
                
                if (apidata[0].sumOfLegendValues?.[0]?.sumOfLegendValue) {
                    setSumOfLegendValue(apidata[0].sumOfLegendValues[0].sumOfLegendValue);
                }
            } catch {
                setLegendItems([]);
            }

            // Process chart data
            try {
                const columnDataSet1 = apidata[0].columnDataSet1?.[0];
                const columnDataSet2 = apidata[0].columnDataSet2?.[0];
                const columnDataSet4 = apidata[0].columnDataSet4?.[0];

                // Helper function to convert text to title case
                const toTitleCase = (str) => {
                    return str
                        .toLowerCase()
                        .split('-')
                        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
                        .join(' ');
                };

                // Extract labels from legend array and convert to title case
                const legendArray = apidata[0].legend || [];
                const datasetLabel1 = legendArray[0]?.legendText ? toTitleCase(legendArray[0].legendText) : 'Distributor';
                const datasetLabel2 = legendArray[1]?.legendText ? toTitleCase(legendArray[1].legendText) : 'Sub Distributor';
                const datasetLabel4 = legendArray[2]?.legendText ? toTitleCase(legendArray[2].legendText) : 'Bonus';

                if (columnDataSet1 || columnDataSet2) {
                    const hasData = (columnDataSet1?.data?.length > 1) || (columnDataSet2?.data?.length > 1);
                    
                    if (!hasData) {
                        setNoDataMessage('No Data Available');
                    } else {
                        setNoDataMessage('');
                    }

                    // Check for negative values
                    const negatives = columnDataSet1?.data?.some(item => 
                        (typeof item === 'object' ? item.y : item) < 0
                    );
                    setHasNegativeValues(negatives || false);

                    // Filter logic: Remove data points where both DISTRIBUTOR and SUB-DISTRIBUTOR are 0
                    const filteredLabels = [];
                    const filteredData1 = [];
                    const filteredData2 = [];
                    const filteredData4 = [];

                    const labels = columnDataSet1?.labels || columnDataSet2?.labels || [];
                    const data1 = columnDataSet1?.data || [];
                    const data2 = columnDataSet2?.data || [];
                    const data4 = columnDataSet4?.data || [];

                    labels.forEach((label, index) => {
                        const value1 = typeof data1[index] === 'object' ? data1[index]?.y : data1[index];
                        const value2 = typeof data2[index] === 'object' ? data2[index]?.y : data2[index];
                        
                        // Only include if at least one value is non-zero
                        if (value1 !== 0 || value2 !== 0) {
                            filteredLabels.push(label);
                            filteredData1.push(data1[index]);
                            filteredData2.push(data2[index]);
                            filteredData4.push(data4[index]);
                        }
                    });

                    const datasets = [];

                    if (filteredData1.length > 0 && columnDataSet1) {
                        datasets.push({
                            label: datasetLabel1,
                            data: filteredData1,
                            backgroundColor: themeColors.secondary,
                            borderColor: themeColors.secondary,
                            borderRadius: 4,
                            stack: 'stack1',
                        });
                    }

                    if (filteredData2.length > 0 && columnDataSet2) {
                        datasets.push({
                            label: datasetLabel2,
                            data: filteredData2,
                            backgroundColor: themeColors.primary,
                            borderColor: themeColors.primary,
                            borderRadius: 4,
                            stack: 'stack1',
                        });
                    }

                    if (filteredData4.length > 0 && columnDataSet4) {
                        datasets.push({
                            label: datasetLabel4,
                            data: filteredData4,
                            backgroundColor: themeColors.extra,
                            borderColor: themeColors.extra,
                            borderRadius: 4,
                            stack: 'stack1',
                        });
                    }

                    setChartData({
                        labels: filteredLabels,
                        datasets,
                    });
                }
            } catch (err) {
            }

            // Process list data
            try {
                const listDataSet = apidata[0].listDataSet;
                if (listDataSet && listDataSet.length > 0) {
                    const headers = listDataSet[0]?.datasets?.map(d => d.data) || [];
                    setListHeaders(headers);

                    const rows = listDataSet.slice(1).map(row => 
                        row.datasets?.map(d => d.data) || []
                    );
                    setListData(rows);
                }
            } catch (err) {
            }

            setIsLoading(false);
        } catch (err) {
            if (err.name === 'AbortError') return;
            setError('Error loading data');
            setIsLoading(false);
        }
    }, [data.url, data.RequestJson, data.RequestJson1, handleResponse, themeColors]);

    // Fetch data when RequestJson changes (with null guard)
    useEffect(() => {
        if (data.type === 'MultiBar_List' && data.RequestJson && data.url && 
            data.RequestJson !== prevRequestJsonRef.current) {
            prevRequestJsonRef.current = data.RequestJson;
            fetchData();
        }
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Initial fetch (with null guard and proper deps)
    useEffect(() => {
        if (data.type === 'MultiBar_List' && data.RequestJson && data.url) {
            fetchData();
        }
        
        return () => {
            if (abortControllerRef.current) {
                abortControllerRef.current.abort();
            }
        };
    }, [data.type, data.RequestJson, data.url, fetchData]);

    // Format legend value
    const formattedLegendValue = useMemo(() => {
        if (!sumOfLegendValue) return '';
        
        const showDollar = data.title !== 'Client Access Link Signups' && 
                          data.title !== 'New vs Repeat Transactions';
        
        return showDollar ? `$${sumOfLegendValue}` : sumOfLegendValue;
    }, [sumOfLegendValue, data.title]);

    // Render chart content
    const chartContent = useMemo(() => {
        if (!chartData || !chartData.labels?.length) {
            return null;
        }

        return (
            <div className="h-full w-full flex flex-col">
                {/* Legend items */}
                {legendItems.length > 0 && (
                    <div className="flex flex-wrap gap-4 mb-4 text-sm justify-center">
                        {legendItems.map((item, idx) => (
                            <div key={idx} className="flex items-center gap-2">
                                <span 
                                    className="w-3 h-3 rounded-sm" 
                                    style={{ backgroundColor: item.color }}
                                />
                                <span className="font-semibold">{item.value}</span>
                                <span className="text-muted-foreground">{item.text}</span>
                            </div>
                        ))}
                    </div>
                )}
                
                {/* Chart */}
                <div className="flex-1 min-h-0">
                    <Bar 
                        data={chartData} 
                        options={convertChartOptions(chartOptions)} 
                    />
                </div>
            </div>
        );
    }, [chartData, chartOptions, legendItems]);

    return (
        <ChartWidget
            title={data.title}
            subtitle={data.Subtitle}
            legendValue={formattedLegendValue}
            isLoading={isLoading}
            error={error}
            noDataMessage={noDataMessage || 'No data available'}
            chartContent={chartContent}
            listData={listData}
            listHeaders={listHeaders}
            onExport={handleExport}
            exportTitle={data.title}
            showToggle={true}
            defaultView="chart"
            minHeight="300px"
        />
    );
}

export default MultiStackBarChart;

